import type { ReactNode, JSX } from 'react'

import { Flex, Text } from '@buffer-mono/popcorn'

import { channelIconElement } from '../ChannelLegendItem'
import type { ChannelLegendData } from '../ChannelLegendItem'

import { ChartColorDot } from './ChartColorDot'
import { formatEventDate } from './eventDateAnnotations'

/**
 * Compound building blocks for a timeline flag's popover body. The caller
 * composes them and decides the per-row phrasing, so the popover isn't tied to
 * any one kind of event:
 *
 * ```tsx
 * <AnnotationPopover>
 *   <AnnotationPopover.DateGroup date={day.eventDate}>
 *     {day.channels.map((ch) => (
 *       <AnnotationPopover.ChannelRow key={ch.id} channel={ch}>
 *         was connected
 *       </AnnotationPopover.ChannelRow>
 *     ))}
 *   </AnnotationPopover.DateGroup>
 * </AnnotationPopover>
 * ```
 */
function AnnotationPopoverRoot({
  children,
}: {
  children: ReactNode
}): JSX.Element {
  return (
    <Flex direction="column" gap="sm">
      {children}
    </Flex>
  )
}

function DateGroup({
  date,
  children,
}: {
  date: Date
  children: ReactNode
}): JSX.Element {
  return (
    <Flex direction="column" gap="2xs">
      <Text size="sm" color="subtle">
        {formatEventDate(date)}
      </Text>
      {children}
    </Flex>
  )
}

function ChannelRow({
  channel,
  children,
}: {
  channel: ChannelLegendData
  children: ReactNode
}): JSX.Element {
  return (
    <Flex align="center" gap="xs">
      <ChartColorDot color={channel.color} />
      {channelIconElement(channel.service)}
      <Text size="sm">
        {channel.name} {children}
      </Text>
    </Flex>
  )
}

export const AnnotationPopover = Object.assign(AnnotationPopoverRoot, {
  DateGroup,
  ChannelRow,
})
